import type { JSX, CSSProperties } from 'react';

import * as styles from 'pkg/config/styles';

import * as models from 'pkg/api/models';
import { initials as accountInitials } from 'pkg/api/models/account/fmt';
import { initials as userInitials } from 'pkg/api/models/user/fmt';
import { cssClasses } from 'pkg/css/utils';

import Logo from 'components/Logo';
import MaterialSymbol, { IconSizeMap } from 'components/material-symbols';

import * as css from './styles.css';

interface AvatarProps {
	author?: models.author.Author;
	user?: models.user.User;
	isEditable?: boolean;
	customImageUrl?: string;
	account?: models.account.Account;
	size?: number;
	testid?: string;
	renderEmptyWith?: JSX.Element;
	className?: string;
	onClick?: () => void;
	style?: CSSProperties;
}

const Avatar = ({
	author,
	user,
	isEditable,
	customImageUrl = '',
	account,
	size,
	testid = 'avatar',
	renderEmptyWith = null,
	className,
	onClick,
	style,
}: AvatarProps): JSX.Element => {
	let initials;
	let imageUrl;
	let tsLogo;

	if (account) {
		initials = accountInitials(account);
		imageUrl = account.profileImageUrl;
	}

	if (user) {
		initials = userInitials(user);
		imageUrl = user.profileImageUrl;
	}

	if (author) {
		initials = author.initials;
		imageUrl = author.profileImageUrl;
		tsLogo = author.type === models.author.AuthorType.TSPlayer;
	}

	if (customImageUrl) {
		imageUrl = customImageUrl;
	}

	let avatar: JSX.Element;

	if (tsLogo) {
		avatar = (
			<div className={css.logo}>
				<Logo width={size} />
			</div>
		);
	} else if (imageUrl?.length > 0) {
		// Avatar image
		avatar = (
			<img
				className={css.image}
				src={imageUrl}
				data-pin-nopin="true"
				loading="lazy"
			/>
		);
	} else if (!initials && !imageUrl) {
		// No user or account, show default icon
		avatar = (
			<div className={css.noUserOverlay}>
				<MaterialSymbol variant="person" fill={styles.palette.blue[500]} />
			</div>
		);
	} else {
		// Initials
		avatar = renderEmptyWith || (
			<svg className={css.initials} viewBox="0 0 100 100">
				<text x="50" y="64" textAnchor="middle">
					{initials}
				</text>
			</svg>
		);
	}

	// Show background only if no image URL is present
	const showBackgroundColor = !imageUrl;

	return (
		<div
			className={cssClasses(
				className,
				css.wrapper,
				showBackgroundColor && css.showBackgroundColor
			)}
			style={{
				width: size ? `${size}px` : undefined,
				...style,
			}}
			data-testid={testid}
			onClick={onClick}>
			<div className={css.container}>
				{avatar}
				{isEditable && (
					<div className={css.overlay}>
						<MaterialSymbol size={IconSizeMap.default} variant="upload" />
					</div>
				)}
			</div>
		</div>
	);
};

export default Avatar;
